import MDEditor, { commands, ICommand, TextState, TextAreaTextApi, MDEditorProps } from '@uiw/react-md-editor';
import Icon from 'components/atoms/Icon';
import classNames from 'classnames';
import CloudinaryImageUpload from 'components/organisms/CloudinaryImageUpload';
import Select from 'components/molecules/Select';
import { DiscoverItemType, KitResource } from 'utils/gloo-types';
import { useEffect, useState } from 'react';
import { kitResourceSelectOptions } from 'utils/discover';
import { useIsMountedRef } from 'utils/hooks';
import { omit } from 'lodash';
import Typography from '../Typography';

/*
 * React Markdown Editor Implementation
 *
 * @doc https://github.com/uiwjs/react-md-editor
 */

function WysiwygEditor(
  props: MDEditorProps & {
    className?: string;
    resources?: KitResource[];
    minDivHeight?: string;
    titleText?: string;
    subtitleText?: string;
  },
) {
  const proTip: ICommand = {
    name: 'proTip',
    keyCommand: 'proTip',
    buttonProps: { 'aria-label': 'Insert pro-tip' },
    icon: <Icon name="thumbs-up" size={12} />,
    execute: (state: TextState, api: TextAreaTextApi) => {
      const proTipped = `
<div class='pro-tip'>
  <div class='image-and-label'>
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-thumbs-up"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>
    <span class='pro-tip-label'>PRO-TIP:</span>
  </div>
  <span class='pro-tip-text'>${state.selectedText}</span>
</div> `.trim();
      api.replaceSelection(proTipped);
    },
  };

  const cloudImg: ICommand = {
    name: 'cloudImg',
    groupName: 'cloudImg',
    keyCommand: 'cloudImg',
    buttonProps: { 'aria-label': 'Insert Cloud Image' },
    icon: <Icon name="upload-cloud" size={12} />,
    children: (handle) => {
      return (
        <div className="px-2">
          <Typography className="p-2" variant="xs">
            Upload an Image
          </Typography>
          <CloudinaryImageUpload
            className="mx-2 mb-2"
            handleImgSelect={(imgUrl) => {
              handle.textApi.replaceSelection(`![](${imgUrl})`);
              handle.close();
            }}
          />
        </div>
      );
    },
  };

  const resourceLink: ICommand = {
    name: 'resourceLink',
    groupName: 'resourceLink',
    keyCommand: 'resourceLink',
    buttonProps: { 'aria-label': `Insert Resource Link` },
    icon: <Icon name="book-open" size={12} />,
    children: (handle) => {
      return (
        <div className="p-2 min-w-96">
          {props?.resources?.length > 0 ? (
            <>
              <Typography className="p-2" variant="xs">
                Select your resource
              </Typography>
              <Select
                dataDurableId="wysiwygResourceSelect"
                options={kitResourceSelectOptions(props.resources)}
                onChange={(option) => {
                  const state = handle.getState();
                  const resource = option.value as KitResource;
                  const resId = resource.type === DiscoverItemType.KIT ? resource.kit.id : resource.template.id;
                  handle.textApi.replaceSelection(
                    `<button type='button' resourceButton resourceId="${resId}" resourceType="${resource.type}">
                      ${state && state.selectedText}
                    </button>`,
                  );
                  handle.close();
                }}
              />
            </>
          ) : (
            <Typography className="p-2" variant="xs">
              No Resources Found
            </Typography>
          )}
        </div>
      );
    },
  };

  // Removes subtitleText and titleText from props to prevent them from polluting the dom on spread
  const updatedProps = omit({ ...props }, ['titleText', 'subtitleText', 'minDivHeight']);

  const [isLoaded, setIsLoaded] = useState(true);
  const isMountedRef = useIsMountedRef();

  // Force the toolbar to refresh
  useEffect(() => {
    if (isMountedRef.current) {
      setIsLoaded(false);
      setTimeout(() => {
        setIsLoaded(true);
      }, 10);
    }
  }, [props.resources]);

  return (
    <div style={{ minHeight: props.minDivHeight }}>
      {props.titleText && (
        <Typography
          className={`text-sm text-glooCharcoal ${props.subtitleText ? 'mb-1' : 'mb-2'} font-semibold`}
          variant="h5"
        >
          {props.titleText}
        </Typography>
      )}
      {props.subtitleText && (
        <Typography className="text-glooMedGray mb-1" variant="base">
          {props.subtitleText}
        </Typography>
      )}
      {isLoaded && (
        <MDEditor
          previewOptions={{ prefixCls: 'wysiwyg' }}
          commands={[
            {
              ...commands.bold,
              icon: <Icon name="bold" size={12} />,
            },
            {
              ...commands.italic,
              icon: <Icon name="italic" size={12} />,
            },
            commands.group(
              [commands.title1, commands.title2, commands.title3, commands.title4, commands.title5, commands.title6],
              {
                name: 'title',
                groupName: 'title',
                buttonProps: { 'aria-label': 'Insert title', className: 'wysiwyg-command' },
                icon: <Icon name="type" size={12} />,
              },
            ),
            commands.unorderedListCommand,
            commands.orderedListCommand,
            {
              ...commands.link,
              icon: <Icon name="link" size={12} />,
            },
            {
              ...commands.image,
              icon: <Icon name="image" size={12} />,
            },
            commands.group([], {
              ...cloudImg,
            }),
            proTip,
            commands.group([], resourceLink),
          ]}
          extraCommands={[]}
          className={classNames('wysiwyg', props.className)}
          {...updatedProps}
        />
      )}
      <MDEditor.Markdown {...updatedProps} />
    </div>
  );
}

export default WysiwygEditor;
